import Component from 'core/Component';
import { Event } from 'services/EventEmitter';
import { on, off, trigger } from 'toolbox/event';
import { deepMerge } from 'toolbox/deepMerge';
import { ajax } from 'toolbox/ajax';
import { mix } from 'core/mixwith';
import { isFiresTouchEvents } from 'toolbox/device';
import LoaderMixin from 'mixins/Loader';
import Accessibility from 'mixins/Accessibility';

/**
 * This is a description of the Paging constructor function.
 * @class
 * @classdesc This is a description of the Paging class.
 * @extends Component
 */
export default class Paging extends mix(Component).with(LoaderMixin, Accessibility) {
    /**
     * Constructor of the class that mainly merge the options of the components
     *
     * @param {HTMLElement} element HTMLElement of the component
     * @param {Object} options That belongs to the component
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            templateName: 'search/paging',
            classNames: {
                loading: 'm-loading',
            },
            observerOptions: {
                rootMargin: '0px 0px 50% 0px',
                threshold: 0,
            },
            events: {
                update: 'search.paging.update',
                updated: 'search.updated',
            },
            isLocalEvents: false, // to be able to have several paging on same page need to fire local events
            insertMode: false,
            isInfiniteScroll: false,
            modelId: 'paging',
            resultModelId: 'searchgrid',
            pagingType: null,
            resultTemplate: null,
            scrollTopOnUpdate: true,
            containerId: null,
            isContentPaging: false, // TO BE DEPRECATED (replaced by useAutoLoadResult)
            useAutoLoadResult: false,
            /* 'useAutoLoadResult' is used to indicate to load result pages itself and not to delegate
            *  to another FE comp(for example searchgrid) which handles the result list container (via event.Emit),
            *  is equivalent to 'isContentPaging' which is TO DEPRECATE
            *  If 'useAutoLoadResult' is true, 'containerId', 'resultTemplate', 'resultModelId' are needed
            */
            scrollTopOffset: 120, // Change the edge offset If you have a fixed navigation bar
        }, options));
    }

    /**
     * Init the different state of the component
     * It helps to avoid heavy DOM manipulation
     */
    initState() {
        this.isInfiniteScroll = this.options.isInfiniteScroll && this.options.pagingType !== 'paging';
        this.state.isKeyboardControl = false;
        if (this.isInfiniteScroll) {
            this.nextPageURL = this.options.nextPageURL;
            this.initObserver();
        }
        if (this.options.pagingType === 'loadmore') {
            this.targetContainer = this.selectors.loadButton;
        } else if (this.options.pagingType === 'paging') {
            this.targetContainer = this.selectors.resultsContainer;
        } else {
            this.targetContainer = this.element;
        }
    }

    /**
     * All selectors must be cached. Never cache elements that are out of the component scope
     */
    initCache() {
        this.selectors.loadButton = this.element.querySelector('[data-js-load-more]');
        this.selectors.pageLinks = this.element.querySelectorAll('[data-js-link]');
        this.selectors.resultsContainer = this.options.containerId ? document.querySelector(`#${this.options.containerId}`) : null;
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonymous function
     */
    bindEvents() {
        if (this.options.isLocalEvents) {
            on(this.options.events.updated, this.element, this.onSearchUpdated.bind(this));
        } else {
            Event.on(this.options.events.updated, this.onSearchUpdated, this);
        }
        on('click', this.selectors.pageLinks, this.onClick.bind(this));
        if (this.selectors.loadButton) {
            on('keydown', this.selectors.loadButton, this.onKeyboardControl.bind(this));
        }
    }

    /**
     * Initialize IntersectionObserver for infinite scroll
     */
    initObserver() {
        this.observer = new window.IntersectionObserver(this.onObserve.bind(this), this.options.observerOptions);
        this.observer.observe(this.element);
    }

    /**
     * Update pagination state after search update
     *
     * @param {Object} data Model for search components
     */
    onSearchUpdated(data) {
        if (!this.options.isContentPaging && !this.options.useAutoLoadResult) {
            data = this.options.isLocalEvents ? data.detail : data;
            this.update(data);
        }
    }

    /**
     * Update pagination state after search update
     *
     * @param {Object} data Model for search components
     */
    onUpdated(data) {
        data = deepMerge(data, {
            insertMode: this.options.insertMode,
            scrollTopSpeed: this.options.scrollTopSpeed,
            scrollTopOffset: this.options.scrollTopOffset,
        });

        this.update(data);
        this.loadContent(data);
    }

    /**
     * Update pagination state after search update
     *
     * @param {Object} data Model for search components
     */
    update(data) {
        const pagingModel = data && data[this.options.modelId];

        if (pagingModel && !this.isInfiniteScroll) {
            this.render(pagingModel).then(this.focusContentAdded(data.template));
        } else if (pagingModel) {
            this.nextPageURL = pagingModel.componentOptions && pagingModel.componentOptions.nextPageURL;
            this.options.insertMode = true;
        }
        this.removeLoader(this.targetContainer);
    }


    /**
     * Load handlebar template for content result
     *
     * @param {Object} data Data model for template rendering
     */
    loadContent(data) {
        if (this.selectors.resultsContainer && this.options.resultTemplate) {
            this.render(data[this.options.resultModelId], {
                templateName: this.options.resultTemplate,
                element: this.selectors.resultsContainer,
                replaceChildren: true,
                isAppend: !!this.options.insertMode,
            }).then(this.focusContentAdded(this.selectors.resultsContainer));

            if (this.options.scrollTopOnUpdate && !this.options.insertMode) {
                this.scrollResultTop(data);
            }
        }
    }

    /**
     * KeyDown event handler
     *
     * @param {Object} event Event object
     */
    onKeyboardControl(event) {
        if (['Enter', ' '].indexOf(event.key) !== -1) {
            this.state.isKeyboardControl = true;
            this.onClick(event);
        }
    }

    /**
     * Fosus on the first element of added content
     *
     * @param {HTMLElement} template with result items
     */
    focusContentAdded(template) {
        if (!template || !this.state.isKeyboardControl) {
            return;
        }

        // Init mutation observer to capture new content
        new MutationObserver(this.onPageContentAdded.bind(this)).observe(
            template,
            { childList: true },
        );

        this.state.isKeyboardControl = false;
    }

    /**
     * Called when a series of dom mutations occur.
     * Sets the keyboard focus on the first new displayed element
     * @param {Array<MutationRecord>} mutations an array of MutationRecords given by the mutation observer
     * @param {Object} observer MutationObserver instance
     */
    onPageContentAdded(mutations, observer) {
        Array.from(mutations[0].addedNodes).some((node) => {
            // Find a first DOM node element
            if (node.tagName) {
                // Set the keyboard focus on the first focusable element of the node
                const focusableElements = this.getFocusableElements(node);
                if (focusableElements) {
                    focusableElements[0].focus();
                    // disconnect the observer and stop execution if the focus is set
                    observer.disconnect();
                    return true;
                }
            }
            return false;
        });
    }

    /**
     * Render content result
     *
     * @param {Object} data due to search updated
     * @param {Object} template with data
     */
    renderResults(data, template) {
        if (!data.resultContainer) {
            return;
        }

        const contentTiles = template.default(data[this.options.resultModelId]);
        // Generate fragment to setup the different classes and observers before it's inject in the DOM three
        const fragment = document.createRange().createContextualFragment(contentTiles);

        if (!data.insertMode) {
            data.resultContainer.innerHTML = '';
        }

        data.resultContainer.appendChild(fragment);
        Event.emit('registry.registerChildren', data.resultContainer);
    }

    /**
     * Scrolls to top of content results
     * @param {Object} data contains required parameters such as setup options and result container
     */
    scrollResultTop(data = {}) {
        if (data.paging.componentOptions.containerId) {
            const container = document.getElementById(data.paging.componentOptions.containerId);
            const topOfElement = window.scrollY + container.getBoundingClientRect().top - this.options.scrollTopOffset;
            window.scroll({ top: topOfElement, behavior: 'smooth' });
        }
    }

    /**
     * Load next page when observer sentinel appears in view port.
     *
     * @param {Object[]} entries IntersectionObserverEntry objects
     */
    onObserve(entries) {
        if (entries[0].intersectionRatio <= 0 || !this.nextPageURL || this.state.isLoading) {
            if (!this.state.isLoading) {
                this.element.innerHTML = '';
            }
            return;
        }
        this.addLoader(this.targetContainer, { overlay: true });
        this.updatePaging(this.nextPageURL);
    }

    /**
     * Handler on click on page link
     *
     * @param {Object} e HTML Event object
     */
    onClick(e) {
        e.preventDefault();

        if (e && isFiresTouchEvents(e)) {
            this.state.isKeyboardControl = true;
        }
        if (this.state.isLoading) {
            return;
        }
        const target = e.currentTarget;
        const url = target.getAttribute('href');
        if (target.dataset.viewAll) {
            this.targetContainer = this.element;
            this.element.innerHTML = '';
            this.addLoader(this.targetContainer, { overlay: true });
            this.initializeViewAll();
        }
        if (url) {
            this.updatePaging(url);
        }
    }

    /**
     * Initialize "View All"
     */
    initializeViewAll() {
        this.isInfiniteScroll = this.options.isInfiniteScroll;

        if (this.isInfiniteScroll) {
            off('click', this.selectors.pageLinks);
            this.initObserver();
        }
    }

    /**
     * Loads a new page
     *
     * @param {String} url New page URL
     */
    updatePaging(url) {
        let loaderOptions = {};
        if (this.options.pagingType !== 'loadmore') {
            loaderOptions = { overlay: true };
        }
        if (this.options.pagingType !== 'paging') {
            this.addLoader(this.targetContainer, loaderOptions);
        }
        if (this.options.isContentPaging || this.options.useAutoLoadResult) {
            this.addLoader(this.targetContainer, loaderOptions);
            ajax(url)
                .then(this.onUpdated.bind(this))
                .catch(this.onUpdateFailed.bind(this));
        } else if (this.options.isLocalEvents) {
            trigger(this.options.events.update, this.element, {
                bubbles: true,
                insertMode: this.isInfiniteScroll ? true : this.options.insertMode,
                paging: true,
                url,
            });
        } else {
            Event.emit(this.options.events.update, {
                insertMode: this.isInfiniteScroll ? true : this.options.insertMode,
                paging: true,
                url,
            });
        }
    }

    /**
     * Callback when a search failed
     */
    onUpdateFailed() {
        if (this.options.isLocalEvents) {
            trigger('search.failed', this.element, { bubbles: true });
        } else {
            Event.emit('search.failed');
        }
    }

    /**
     * Destroy is called automatically after the component is being removed from the DOM
     * You must always destroy the listeners attached to an element to avoid any memory leaks
     */
    destroy() {
        if (this.options.isLocalEvents) {
            off(this.options.events.updated, this.element);
        } else {
            Event.removeListener(this.options.events.updated, this.onSearchUpdated, this);
        }
        off('click', this.selectors.pageLinks);
        if (this.selectors.loadButton) {
            off('keydown', this.selectors.loadButton);
        }
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}
